import { z } from 'zod'

import { isoDateTime } from '../lib/date'
import { type AppointmentTypeId, appointmentTypeIdSchema } from './appointment-type'
import { regionIdSchema } from './region'
import { staffMemberSchema } from './staff'

type ScheduleBlockBase = {
  name: string
  /** Clinic wall-clock time (HH:MM). */
  startTime: string
  /** Clinic wall-clock time (HH:MM). */
  endTime: string
}

/**
 * A single bookable window in a vet's day. Either an explicit allowlist of
 * appointment types, or an exclusion list (all other types allowed).
 */
export type ScheduleBlock =
  | (ScheduleBlockBase & {
      /** Explicit allowlist. Empty array means all types are allowed. */
      appointmentTypesAllowed: AppointmentTypeId[]
      appointmentTypesExcluded?: never
    })
  | (ScheduleBlockBase & {
      /** Types that are explicitly excluded; all others are allowed. */
      appointmentTypesExcluded: AppointmentTypeId[]
      appointmentTypesAllowed?: never
    })

// Real 24h clock: hours 00-23, minutes 00-59 (zero-padded).
const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/

const scheduleBlockBaseSchema = z.object({
  name: z.string().min(1),
  /** Clinic wall-clock HH:MM. */
  startTime: z.string().regex(TIME_REGEX),
  endTime: z.string().regex(TIME_REGEX),
})

/**
 * One bookable window: either an explicit allowlist of appointment types, or an
 * exclusion list (all others allowed). Mirrors the ScheduleBlock union type.
 * The refine (applied after the union) rejects a zero-or-negative-length window —
 * string comparison is correct here since both times are zero-padded HH:MM.
 */
export const scheduleBlockSchema = z
  .union([
    scheduleBlockBaseSchema.extend({
      appointmentTypesAllowed: z.array(appointmentTypeIdSchema),
      appointmentTypesExcluded: z.never().optional(),
    }),
    scheduleBlockBaseSchema.extend({
      appointmentTypesExcluded: z.array(appointmentTypeIdSchema),
      appointmentTypesAllowed: z.never().optional(),
    }),
  ])
  .refine((b) => b.startTime < b.endTime, { message: 'startTime must be before endTime' })

/** A full template's block list — at least one block. */
export const scheduleBlocksSchema = z.array(scheduleBlockSchema).min(1)

/** A named schedule template as returned to the client. */
export const scheduleTemplateSchema = z.object({
  id: z.uuid(),
  /** Only rotas and shifts at this region's clinics can use it. */
  region: regionIdSchema,
  name: z.string(),
  blocks: z.array(scheduleBlockSchema),
  /** Null while the template is offered in pickers. */
  archivedAt: isoDateTime.nullable(),
})

export type ScheduleTemplate = z.output<typeof scheduleTemplateSchema>

/** The set of staff working at a clinic on a given day, with their schedule blocks. */
export const dayScheduleSchema = z.array(
  z.object({
    staff: staffMemberSchema.pick({ id: true, firstName: true, lastName: true, email: true }),
    /** Name of the schedule template this assignment uses (for display). */
    templateName: z.string().optional(),
    schedule: z.array(scheduleBlockSchema),
  })
)

export type DaySchedule = z.output<typeof dayScheduleSchema>
